import React, {useState} from 'react';
import {
    Drawer, List, ListItem, ListItemIcon, ListItemText, Typography,
    Box, Divider, Badge, Button, Dialog, DialogTitle, DialogContent,
    DialogActions, Alert, Tooltip
} from '@mui/material';
import {
    GetApp as ExportIcon,
    Publish as ImportIcon,
    CloudUpload as UploadIcon,
    DeleteSweep as ClearIcon,
    Info as AboutIcon,
    School as WelcomeIcon,
    Language as LanguageIcon,
    EmojiEvents as BadgeIcon,
    Timeline as AnalyticsIcon,
    Share as ShareIcon
} from '@mui/icons-material';
import ShareDialog from './ShareDialog';

const SideMenu = ({
                      open,
                      onClose,
                      onShowWelcome,
                      onExportData,
                      onImportData,
                      onUploadToWikidata,
                      onShowInfo,
                      onShowBadges,
                      onShowStatistics,
                      savedPlaces,
                      selectedLanguage,
                      customLanguageObject,
                      statistics
                  }) => {
    const [clearDialogOpen, setClearDialogOpen] = useState(false);
    const [shareDialogOpen, setShareDialogOpen] = useState(false);

    const handleClearData = () => {
        setClearDialogOpen(true);
    };

    const handleConfirmClear = () => {
        localStorage.removeItem('endolink_welcome_seen');
        // Clear saved places would need to be handled by parent
        setClearDialogOpen(false);
        onClose();
        // Could trigger a callback to parent to clear data
    };

    const getLanguageInfo = () => {
        if (customLanguageObject) {
            return {
                name: customLanguageObject.customLabel || 'Custom Language',
                code: customLanguageObject.customLanguageCode || 'custom'
            };
        } else if (selectedLanguage) {
            return {
                name: selectedLanguage.wikidataLabel || selectedLanguage.languageLabel || 'Unknown',
                code: selectedLanguage.ietfLanguageTag || 'unknown'
            };
        }
        return {name: 'No language selected', code: ''};
    };

    const languageInfo = getLanguageInfo();
    const contributionCount = savedPlaces ? savedPlaces.size : 0;

    const menuSections = [
        {
            title: "Info",
            items: [
                {
                    icon: <WelcomeIcon/>,
                    text: "Show Welcome Guide",
                    description: "Review how to use EndoLink",
                    action: () => {
                        onShowWelcome();
                        onClose();
                    }
                },
                {
                    icon: <AboutIcon/>,
                    text: "Information",
                    description: "Project information & FAQ",
                    action: () => {
                        onShowInfo();
                        onClose();
                    }
                }
            ]
        },
        {
            title: "Your Data",
            items: [
                {
                    icon: <AnalyticsIcon/>,
                    text: "Research Statistics",
                    description: `${contributionCount} endonyms contributed`,
                    badge: contributionCount > 0 ? contributionCount : null,
                    action: () => {
                        onShowStatistics();
                        onClose();
                    }
                },
                {
                    icon: <BadgeIcon/>,
                    text: "Achievement Badges",
                    description: statistics ? `${statistics.badgesEarned.length} badges earned` : "View your achievements",
                    badge: statistics && statistics.badgesEarned.length > 0 ? statistics.badgesEarned.length : null,
                    action: () => {
                        onShowBadges();
                        onClose();
                    }
                },
                {
                    icon: <ExportIcon/>,
                    text: "Export Data",
                    description: "Download your endonyms as JSON",
                    disabled: contributionCount === 0,
                    disabledTooltip: "Add at least one endonym to export data",
                    action: () => {
                        if (contributionCount > 0) {
                            onExportData();
                            onClose();
                        }
                    }
                },
                {
                    icon: <ImportIcon/>,
                    text: "Import Data",
                    description: "Upload previously saved endonyms",
                    action: () => {
                        onImportData();
                        onClose();
                    }
                }
            ]
        },
        {
            title: "Upload",
            items: [
                {
                    icon: <UploadIcon/>,
                    text: "Upload to Wikidata",
                    description: "Share your contributions globally",
                    disabled: contributionCount === 0,
                    disabledTooltip: "Add at least one endonym to upload to Wikidata",
                    action: () => {
                        if (contributionCount > 0) {
                            onUploadToWikidata();
                            onClose();
                        }
                    }
                }
            ]
        },
        {
            title: "Share",
            items: [
                {
                    icon: <ClearIcon/>,
                    text: "Clear All Data",
                    description: "Reset all saved endonyms",
                    disabled: contributionCount === 0,
                    action: handleClearData,
                    warning: true
                },
                {
                    icon: <ShareIcon/>,
                    text: "Share EndoLink",
                    description: "Help spread the word about this platform",
                    action: () => {
                        setShareDialogOpen(true);
                    }
                }
            ]
        }
    ];

    return (
        <>
            <Drawer
                anchor="left"
                open={open}
                onClose={onClose}
                PaperProps={{
                    sx: {
                        width: 320,
                        background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                        borderRight: '1px solid rgba(226, 232, 240, 0.8)'
                    }
                }}
            >
                {/* Header */}
                <Box sx={{
                    p: 3,
                    background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)',
                    color: 'white'
                }}>
                    <Typography variant="h6" sx={{fontWeight: 700, mb: 1}}>
                        EndoLink Menu
                    </Typography>

                    {/* Current Language */}
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        backgroundColor: 'rgba(255, 255, 255, 0.15)',
                        borderRadius: 2,
                        p: 1.5,
                        mt: 2
                    }}>
                        <LanguageIcon sx={{fontSize: 20}}/>
                        <Box sx={{flex: 1}}>
                            <Typography variant="body2" sx={{fontWeight: 500, opacity: 0.9}}>
                                Current Language
                            </Typography>
                            <Typography variant="body2" sx={{fontSize: '0.85rem'}}>
                                {languageInfo.name} {languageInfo.code && `(${languageInfo.code})`}
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                {/* Menu Sections */}
                <Box sx={{flex: 1, overflow: 'auto'}}>
                    {menuSections.map((section, sectionIndex) => (
                        <Box key={section.title}>
                            <Typography variant="overline" sx={{
                                px: 3,
                                py: 2,
                                color: '#64748b',
                                fontWeight: 600,
                                fontSize: '0.75rem',
                                display: 'block'
                            }}>
                                {section.title}
                            </Typography>

                            <List sx={{py: 0}}>
                                {section.items.map((item, index) => {
                                    const listItem = (
                                        <ListItem
                                            key={index}
                                            button
                                            onClick={item.action}
                                            disabled={item.disabled}
                                            sx={{
                                                px: 3,
                                                py: 1.5,
                                                '&:hover': {
                                                    backgroundColor: item.warning ?
                                                        'rgba(239, 68, 68, 0.08)' :
                                                        'rgba(37, 99, 235, 0.08)'
                                                },
                                                ...(item.disabled && {
                                                    opacity: 0.5
                                                })
                                            }}
                                        >
                                            <ListItemIcon sx={{
                                                minWidth: 40,
                                                color: item.warning ? 'error.main' : 'primary.main'
                                            }}>
                                                {item.badge ? (
                                                    <Badge badgeContent={item.badge} color="primary">
                                                        {item.icon}
                                                    </Badge>
                                                ) : item.icon}
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={
                                                    <Typography variant="body2" sx={{
                                                        fontWeight: 500,
                                                        color: item.warning ? 'error.main' : 'text.primary'
                                                    }}>
                                                        {item.text}
                                                    </Typography>
                                                }
                                                secondary={
                                                    <Typography variant="caption" sx={{
                                                        color: '#64748b',
                                                        fontSize: '0.75rem'
                                                    }}>
                                                        {item.description}
                                                    </Typography>
                                                }
                                            />
                                        </ListItem>
                                    );

                                    // Wrap with tooltip if disabled and has disabledTooltip
                                    if (item.disabled && item.disabledTooltip) {
                                        return (
                                            <Tooltip key={index} title={item.disabledTooltip} arrow>
                                                <span>{listItem}</span>
                                            </Tooltip>
                                        );
                                    }

                                    return listItem;
                                })}
                            </List>

                            {sectionIndex < menuSections.length - 1 && (
                                <Divider sx={{mx: 2, my: 1}}/>
                            )}
                        </Box>
                    ))}
                </Box>

                {/* Footer */}
                <Box sx={{
                    p: 2,
                    borderTop: '1px solid rgba(226, 232, 240, 0.8)',
                    backgroundColor: 'rgba(248, 250, 252, 0.5)'
                }}>
                    <Typography variant="caption" sx={{
                        color: '#64748b',
                        textAlign: 'center',
                        display: 'block',
                        fontSize: '0.65rem',
                        opacity: 0.8,
                        mb: 0.5
                    }}>
                        © 2025 Janine Hindermann
                    </Typography>
                </Box>
            </Drawer>

            {/* Clear Data Confirmation Dialog */}
            <Dialog
                open={clearDialogOpen}
                onClose={() => setClearDialogOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                    <ClearIcon sx={{color: 'error.main'}}/>
                    Clear All Data
                </DialogTitle>
                <DialogContent>
                    <Alert severity="warning" sx={{mb: 2}}>
                        <Typography variant="body2" sx={{fontWeight: 500, mb: 1}}>
                            This action cannot be undone!
                        </Typography>
                        <Typography variant="body2">
                            You will lose all {contributionCount} saved endonyms and need to start over.
                        </Typography>
                    </Alert>
                    <Typography variant="body2" sx={{color: '#64748b'}}>
                        Consider exporting your data first if you want to keep a backup of your contributions.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{p: 3, gap: 1}}>
                    <Button
                        onClick={() => setClearDialogOpen(false)}
                        variant="outlined"
                        sx={{borderRadius: 2}}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleConfirmClear}
                        variant="contained"
                        color="error"
                        sx={{borderRadius: 2}}
                    >
                        Clear All Data
                    </Button>
                </DialogActions>
            </Dialog>

            <ShareDialog
                open={shareDialogOpen}
                onClose={() => setShareDialogOpen(false)}
                shareType="platform"
                statistics={statistics}
            />
        </>
    );
};

export default SideMenu;